import { useEffect, useState, useMemo } from "react";
import AmrMeterGroup from "../amr-meter-group/amr-meter-group";
import PageHierarchy from "../page-hierarchy/page-hierarchy";
import "./other-management.scss";
import { useDispatch, useSelector } from "react-redux";
import NewProductPopup from "../new-product-popup/new-product-popup";
import OtherTable from "../other-table/other-table";
import { deleteOPProductsByShop, getOPProductsByShop, updateOPProductsByShopBulk } from "../../productSlices";
import ConfirmationPopup from "../confirmation-popup/confirmation-popup";
import { Button } from "primereact/button";
import { Dropdown } from 'primereact/dropdown';
import { setSelectedShop } from "features/authentication/userSlices";
import { UpperControls } from "../other-table/upper-controls";
import isEqual from 'lodash/isEqual';
import { StockDestination } from "../stock-table-count-cell/stock-table-count-cell";

const getRandomColor = () => {
  return '#' + Math.floor(Math.random()*16777215).toString(16);
}

const OtherManagement = () => {
  const dispatch: Function = useDispatch();
  const user = useSelector((state: any) => state.auth.user);
  const selectedShop = useSelector((state: any) => state.user.selectedShop);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null)
  const [wordToConfirm, setWordToConfirm] = useState(null)
  const [productIdToDelete, setProductIdToDelete] = useState(null)
  const op_products = useSelector((state: any) => state.product.op_products);
  const meterGroupValues = op_products.map((product:any)=>({label:product.name, value:product.total_quantity, color:getRandomColor()}))
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [modifiedProducts, setModifiedProducts] = useState([]);
  const bulkLoading = useSelector((state: any) => state.product.bulk_loading);
  
  useEffect(()=>{
    dispatch(getOPProductsByShop(selectedShop))
  },[selectedShop])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (op_products.length > 0) {
      setSelectedFilters(Array.from(new Set(op_products.map(product => product.name as string))));
      setModifiedProducts(op_products);
    }
  }, [op_products]);

  const shopTemplate = (option) => {
    if (!option) return null;
    
    return (
      <div className="mobile-shop-selector__option">
        <div className="mobile-shop-selector__option__name">{option.name}</div>
        <div className="mobile-shop-selector__option__address">{option.address}</div>
      </div>
    );
  };

  const selectedShopObject = user.shops.find(shop => shop.id === selectedShop);

  const handleFilterToggle = (productName: string) => {
    setSelectedFilters(prev => 
      prev.includes(productName) 
        ? prev.filter(name => name !== productName)
        : [...prev, productName]
    );
  };

  const handleSelectAllFilters = (checked: boolean) => {
    setSelectedFilters(checked ? uniqueProductNames as string[] : []);
  };

  const uniqueProductNames = useMemo(() => {
    return Array.from(new Set(op_products.map(product => product.name))) as string[];
  }, [op_products]);

  const handleStockModify = (productId: string, field: string, value: number, destinationShop: string, destination: string) => {
    setModifiedProducts(modifiedProducts.map(product => 
        product.id === productId 
            ? { ...product, [field]: value, destination_shop_id: destination === StockDestination.OtherShop ? destinationShop : undefined, has_been_consumed: !(destination === StockDestination.Trash), destination: destination }
            : product
    ));
  };

  const hasModifications = () => {
    
    return !isEqual(modifiedProducts, op_products);
  }

  const onShowModifications = () => {
    const diff = modifiedProducts.filter(product => 
      product.pack_count !== op_products.find(p => p.id === product.id).pack_count || product.total_quantity !== op_products.find(p => p.id === product.id).total_quantity
    );
    dispatch(updateOPProductsByShopBulk(selectedShop, diff));
  }

  return (
    <div className='other-management'>
      <div className="other-management__header">
        <PageHierarchy />
        {/* {!isMobile && user.allowedToEdit && (
          <Button 
            onClick={() => setIsPopupOpen(true)} 
            icon="pi pi-plus" 
            iconPos='left' 
            label={'Add flavors'} 
            className="add-button" 
          />
        )} */}
        {isMobile && (
          <>
            <Dropdown 
              value={selectedShopObject}
              onChange={(e) => dispatch(setSelectedShop(e.value.id))}
              options={user.shops} 
              optionLabel="name" 
              placeholder="Select a shop"
              className="mobile-shop-selector"
              valueTemplate={shopTemplate}
              itemTemplate={shopTemplate}
            />
          </>
        )}
      </div>
      {isMobile && (
        <UpperControls
          search={search}
          setSearch={setSearch}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          handleFilterToggle={handleFilterToggle}
          handleSelectAllFilters={handleSelectAllFilters}
          uniqueProductNames={uniqueProductNames as string[]}
          onShowModifications={onShowModifications}
          bulkLoading={bulkLoading}
          hasModifications={hasModifications}
        />
      )}
      {op_products.length > 0 ? (
        <>
          {/* <AmrMeterGroup values={meterGroupValues} /> */}
          <OtherTable
            setIsPopupOpen={setIsPopupOpen}
            setProductIdToDelete={setProductIdToDelete}
            setWordToConfirm={setWordToConfirm}
            setProductToEdit={setProductToEdit}
            search={search}
            setSearch={setSearch}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            showUpperControls={!isMobile}
            handleStockModify={handleStockModify}
            modifiedProducts={modifiedProducts}
            hasModifications={hasModifications}
            onShowModifications={onShowModifications}
          />
          {(isPopupOpen || productToEdit) && (
            <NewProductPopup
              isVisible={isPopupOpen || !!productToEdit}
              setVisible={setIsPopupOpen}
              productToEdit={productToEdit}
              setProductToEdit={setProductToEdit}
            />
          )}
          {wordToConfirm && productIdToDelete &&
            <ConfirmationPopup
              wordToConfirm={wordToConfirm}
              setWordToConfirm={setWordToConfirm}
              actionText='Are you sure you want to delete this product ?'
              actionDetail='This action is not recovarable'
              fncOnConfirm={deleteOPProductsByShop}
              fncArgs={{shopId:selectedShop, productId:productIdToDelete}}
              />
          }
        </>
      ) : (
        <div className="other-management__no-data">
          <p>No inventory data available. Please select or add a shop first to manage your products.</p>
          <Button 
              label="Go to Shop Management" 
              onClick={() => window.location.href = '/other/shop-management'}
              className="p-button-link"
          />
        </div>
      )}
    </div>
  );
};

export default OtherManagement;
